import * as React from "react";

import { cn } from "@/lib/utils";

import "./DurationField.css";

/**
 * Duration/interval picker shared by the ad-hoc booking builder and the rolling link builder: a row
 * of quick-select pills (reactbits "Pill Nav" style — the fill grows from the bottom on hover/active)
 * beside a single masked, pill-shaped h:mm field with its live readout right beside it.
 *
 * <h2>Minutes first, then hours</h2>
 *
 * <p>Digits fill the minutes, and once the minutes are full they fill the hours: "4" is 0:04, "45"
 * is 0:45, a following "1" is 1:45, a following "5" is 15:45. Deleting walks back out the same way.
 * A duration is nearly always a number of minutes, so the field spends its first two keystrokes on
 * the part being typed rather than on the part usually left at zero.</p>
 *
 * <p>It used to fill left to right, hours first, which meant a host typing the ordinary "45" got
 * 4:05. Every plain minutes entry was wrong: "30" became 3 hrs, "90" became 9. Hours-first only
 * works when there is a unit to anchor it, and a duration has none until it is read back.</p>
 *
 * <h2>The field says it is a field</h2>
 *
 * <p>It sits in a row of fixed-duration pills that it must not be mistaken for, so it carries a
 * brand-blue outline, a caret resting in it, and the format as its placeholder. The presets are
 * things you press; this is a thing you type into, and it should not need saying twice.</p>
 */

export interface DurationPreset {
  label: string;
  minutes: number;
}

const DEFAULT_PRESETS: DurationPreset[] = [
  { label: "15 mins", minutes: 15 },
  { label: "30 mins", minutes: 30 },
  { label: "1 hr", minutes: 60 },
  { label: "2 hrs", minutes: 120 },
];

/** The most digits any value can need: 24:00 is the largest duration offered anywhere. */
const MAX_DIGITS = 4;

/**
 * The digits in the order they were typed: the first two are the minutes, the next two the hours.
 *
 * <p>Note this is NOT the order they appear on screen. "451" reads 1:45, so the field cannot
 * recover this stream by re-reading its own value, which is why {@link DurationField} tracks what
 * was typed rather than parsing what is displayed.</p>
 */
export function splitDigits(digits: string): { hh: string; mm: string } {
  return { mm: digits.slice(0, 2), hh: digits.slice(2, 4) };
}

/**
 * Total minutes for a digit stream. Minutes above 59 are honest rather than rejected: "1:75" is
 * 2 hrs 15 mins, the readout says so as it is typed, and blurring settles it to 2:15.
 */
export function digitsToMinutes(digits: string): number {
  const { hh, mm } = splitDigits(digits);
  return Number(hh || 0) * 60 + Number(mm || 0);
}

/** What the field shows mid-edit: "" while empty, else h:mm with the minutes zero-padded. */
export function editingDisplay(digits: string): string {
  const { hh, mm } = splitDigits(digits);
  if (!mm) return "";
  return `${hh || "0"}:${mm.padStart(2, "0")}`;
}

/** Canonical display for a committed value: always h:mm (e.g. "0:30", "1:30", "12:00"). */
function formatDuration(minutes: number): string {
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
}

/** Plain-English readout: "3 hrs", "5 mins", "1 hr 30 mins". */
function readout(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const parts: string[] = [];
  if (h) parts.push(`${h} ${h === 1 ? "hr" : "hrs"}`);
  if (m || !h) parts.push(`${m} ${m === 1 ? "min" : "mins"}`);
  return parts.join(" ");
}

export function DurationField({
  minutes,
  onChange,
  min = 5,
  max = 720,
  presets = DEFAULT_PRESETS,
  label = "Duration",
}: {
  minutes: number;
  onChange: (next: number) => void;
  /** Smallest allowed total (minutes). */
  min?: number;
  /** Largest allowed total (minutes). A digit that would exceed it is refused, never silently cut. */
  max?: number;
  /** Quick-select pills; defaults to 15 min / 30 min / 1 hr / 2 hrs. */
  presets?: DurationPreset[];
  /** Names the field for screen readers ("Slot length", "Gap between slots", "Minimum notice"). */
  label?: string;
}) {
  const clamp = (n: number): number => Math.max(min, Math.min(max, n));
  const ref = React.useRef<HTMLInputElement>(null);
  const [focused, setFocused] = React.useState(false);
  const [digits, setDigits] = React.useState("");
  // Whether the whole value was selected when the last key went down. A change cannot tell a
  // select-all-and-retype from a deletion by looking at the result: selecting "0:90" and typing "0"
  // leaves "0", which is also what deleting two digits leaves. The selection before the edit is the
  // only thing that separates them. Left false when no keydown fires (a paste, some phone
  // keyboards), which falls back to the reading below.
  const replacingAll = React.useRef(false);

  const typed = editingDisplay(digits);
  // Rest state shows the committed h:mm, and stays EMPTY at 0 so the placeholder and caret invite
  // typing. Mid-edit the display follows the digits, so what is on screen is always what was typed.
  const display = focused ? typed : minutes > 0 ? formatDuration(minutes) : "";
  // The plain-English readout is the unit feedback: it names hrs and mins while the digits are
  // still going in, which is the whole reason the old hours-first mask could mislead.
  const showReadout = focused ? digits.length > 0 : minutes > 0;
  const readoutMinutes = focused ? digitsToMinutes(digits) : minutes;
  // A resting caret marks the one control in the row that is typed into rather than pressed. Once
  // focused the real caret takes over; the placeholder holds the space so nothing shifts.
  const ghostCaret = !focused && !display;

  const caretToEnd = (): void => {
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el) {
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
    });
  };

  /** Accept a new digit stream unless it would exceed max, in which case the keystroke is refused. */
  const commit = (next: string): void => {
    const capped = next.slice(0, MAX_DIGITS);
    if (digitsToMinutes(capped) > max) return;
    setDigits(capped);
    onChange(clamp(digitsToMinutes(capped)));
    caretToEnd();
  };

  /**
   * Read a change by what it DID to the field, not by parsing the result: digits appended at the
   * caret, or removed by a backspace. The value on screen is not the stream that produced it,
   * since "451" shows as 1:45, so re-reading it would scramble the two.
   *
   * <p>Still driven by the input's value rather than by keystrokes, so a phone's numeric keypad, a
   * paste and a browser autofill all land the same way.</p>
   */
  const onInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const now = e.target.value.replace(/\D/g, "");
    const shown = display.replace(/\D/g, "");
    const fresh = replacingAll.current;
    replacingAll.current = false;
    if (fresh) {
      commit(now);
    } else if (now.length > shown.length && now.startsWith(shown)) {
      commit(digits + now.slice(shown.length));
    } else if (now.length < shown.length && shown.startsWith(now)) {
      commit(digits.slice(0, digits.length - (shown.length - now.length)));
    } else {
      // Neither an append nor a delete, and nothing said the value was replaced: take what is there.
      commit(now);
    }
  };

  return (
    // theme-primary-brand-blue rebinds --primary for this row only. Unwrapped, the pills' fill is
    // the pre-reskin emerald (principle 25 bans it in app UI), which would now sit beside a
    // brand-blue field. Rebinding on a wrapper is the documented fix; forking the pill is not.
    <div className="theme-primary-brand-blue flex flex-wrap items-center gap-2">
      {presets.map((p) => (
        <button
          key={p.minutes}
          type="button"
          aria-pressed={minutes === p.minutes}
          onClick={() => onChange(p.minutes)}
          className="duration-pill px-3 py-1 text-xs font-semibold uppercase tracking-wide"
        >
          {p.label}
        </button>
      ))}

      <div className="flex items-center gap-2">
        <div className={cn("duration-entry", focused && "is-focused")}>
          <span className={cn("duration-caret", !ghostCaret && "invisible")} aria-hidden="true" />
          <input
            ref={ref}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-label={`${label} (h:mm)`}
            placeholder="h:mm"
            value={display}
            onChange={onInput}
            onFocus={() => {
              setDigits("");
              setFocused(true);
            }}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              const el = e.currentTarget;
              replacingAll.current =
                el.value.length > 0 && el.selectionStart === 0 && el.selectionEnd === el.value.length;
              if (e.key === "Enter") {
                e.preventDefault();
                el.blur();
              }
            }}
            className="duration-entry-input"
          />
        </div>
        {showReadout && (
          <span className="whitespace-nowrap text-xs font-semibold text-foreground">
            {readout(readoutMinutes)}
          </span>
        )}
      </div>
    </div>
  );
}
